import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import EntityTable, { StatusBadge, RowActionMenu } from "components/EntityTable";
import ConfirmDialog from "components/ConfirmDialog";
import SidePanel from "components/SidePanel";
import usePaginatedResource from "hooks/usePaginatedResource";
import useDebounce from "hooks/useDebounce";
import { formatDate } from "utils/datetime";
import { listEmailTemplates, deleteEmailTemplate } from "api/endpoints/emailTemplates";

import EmailTemplateForm from "../../UI/EmailTemplateForm";

// Email Templates content view — a server-paginated table with create/edit panel.
const EmailTemplatesPage = ({ websiteId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const [form, setForm] = useState({ open: false, template: null });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchTemplates = useCallback(
    (pageParams) => {
      if (!websiteId) return { items: [], total: 0 };
      return listEmailTemplates(websiteId, { ...pageParams, search: debouncedSearch });
    },
    [websiteId, debouncedSearch]
  );

  const { rows, total, loading, page, pageSize, setPage, setPageSize, refresh } =
    usePaginatedResource(fetchTemplates);

  // A new search term restarts at the first page.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, setPage]);

  const confirmDelete = async () => {
    try {
      await deleteEmailTemplate(websiteId, confirmDeleteId);
      enqueueSnackbar("Template deleted", { variant: "success" });
      refresh();
    } catch {
      enqueueSnackbar("Failed to delete template", { variant: "error" });
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const openCreate = () => setForm({ open: true, template: null });
  const openEdit = (template) => setForm({ open: true, template });
  const closeForm = () => setForm({ open: false, template: null });

  const columns = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 160 },
    {
      field: "subject_template",
      headerName: "Subject",
      flex: 1.6,
      minWidth: 220,
      renderCell: ({ value }) => value || "—",
    },
    {
      field: "created_at",
      headerName: "Created On",
      flex: 0.9,
      minWidth: 140,
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: "active",
      headerName: "Status",
      flex: 0.7,
      minWidth: 120,
      renderCell: ({ value }) => <StatusBadge active={value} />,
    },
    {
      field: "actions",
      headerName: "",
      width: 64,
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <RowActionMenu
          actions={[
            { label: "Edit", icon: "edit", onClick: () => openEdit(row) },
            {
              label: "Delete",
              icon: "delete",
              color: "error",
              onClick: () => setConfirmDeleteId(row.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <MDBox>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <MDTypography variant="h5" fontWeight="bold">
          Email Templates
        </MDTypography>
        <MDButton variant="gradient" color="info" startIcon={<Icon>add</Icon>} onClick={openCreate}>
          New Template
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <MDBox mb={2} maxWidth={320}>
            <MDInput
              fullWidth
              placeholder="Search by name or subject"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{ endAdornment: <Icon fontSize="small">search</Icon> }}
            />
          </MDBox>
          <EntityTable
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            paginationMode="server"
            rowCount={total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onRowClick={({ row }) => openEdit(row)}
          />
        </MDBox>
      </Card>

      <SidePanel
        open={form.open}
        onClose={closeForm}
        title={form.template ? "Edit Template" : "New Template"}
        subtitle="Create a custom communication template"
        width={760}
      >
        <EmailTemplateForm
          existingTemplate={form.template}
          websiteId={websiteId}
          onSuccess={() => {
            closeForm();
            refresh();
          }}
        />
      </SidePanel>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Delete Template"
        message="Are you sure you want to delete this template?"
        confirmText="Delete"
        onConfirm={confirmDelete}
        onClose={() => setConfirmDeleteId(null)}
      />
    </MDBox>
  );
};

EmailTemplatesPage.propTypes = {
  websiteId: PropTypes.string,
};

EmailTemplatesPage.defaultProps = {
  websiteId: undefined,
};

export default EmailTemplatesPage;
